<template>
  <div
    ref="dropzoneContainer"
    class="input-image-zone"
    :class="{
      'no-image': imageData == null,
      'is-hovering': isOverDropZone || processingImage
    }"
  >
    <label :for="fieldId" class="input-image-zone__main" :class="{ 'no-image': imageData == null }">
      <template v-if="imageData">
        <img
          class="input-image-zone__image sidebar__image"
          :src="imageData"
          :style="{ objectFit: objectFit }"
        />
      </template>
      <div class="input-image-zone__dropzone__text">
        <template v-if="!imageData">
          <div v-show="!isOverDropZone && !processingImage">
            <lucide-icon class="input-image-zone__icon" name="plus" />
            <div>
              {{ $t("labels.selectOrDropImage") }}
            </div>
          </div>
        </template>
        <transition name="fadeup">
          <div v-show="isOverDropZone">
            <lucide-icon class="input-image-zone__icon" name="plus" />
            <div>{{ $t("labels.dropHere") }}</div>
          </div>
        </transition>
        <transition name="fadeup">
          <div v-show="processingImage">
            <lucide-icon class="input-image-zone__icon" name="pause" />
            <div>{{ $t("messages.processingFile") }}</div>
          </div>
        </transition>
      </div>
    </label>

    <input
      :id="fieldId"
      type="file"
      class="input-image-zone__input"
      @change="onFileFieldChange"
    />

    <button
      v-if="imageData"
      class="input-image-zone__deletebutton button-icon"
      type="button"
      :title="$t('actions.deleteImage', 1)"
      @click="emit('delete')"
    >
      <lucide-icon name="trash" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef, ref } from "vue";
import { useI18n } from "vue-i18n";
import { fileToDataURL, warnIfFileIsLarge } from "@/helpers/utility/File";
import { showError } from "@core/ui/alert/alert";
import { v4 as uuidv4 } from "uuid";
import { useDropZone } from "@vueuse/core";

import LucideIcon from "@core/ui/LucideIcon.vue";

withDefaults(defineProps<{
  imageData: string | null | undefined;
  fieldId?: string;
  objectFit?: "contain" | "cover" | "none";
  targetWidth?: number;
  targetHeight?: number;
}>(), {
  fieldId: uuidv4(),
  objectFit: "contain",
  targetWidth: 270,
  targetHeight: 200,
});

const emit = defineEmits<{
  (e: "delete"): void;
  (e: "update:imageData", image: string | null): void;
  (e: "change", img: { src: string; width: number; height: number; type: string }): void;
}>();

const { t } = useI18n();
const dropzoneContainer = useTemplateRef("dropzoneContainer");
const processingImage = ref(false);

const allowedFileTypes = [
  "image/svg+xml",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/avif",
  "video/mp4",
  "video/webm",
  "video/ogg",
  "image/*",
  "video/*"
];

const { isOverDropZone } = useDropZone(
  dropzoneContainer,
  {
    onDrop: (files: File[] | null, event) => {
      event.stopPropagation();
      event.preventDefault();
      if (!files) {
        return;
      } else {
        chooseImage(files[0]);
      }
    },
    dataTypes: allowedFileTypes
  }
);

async function onFileFieldChange(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files == null) return;
  try {
    await chooseImage(target.files[0]);
  } catch (error) {
    console.error("Error loading image", error);
  }

  // Reset the input field
  target.value = "";
}


async function chooseImage(file: File | Blob) {
  if (!isFileTypeAllowed(file.type)) {
    console.warn("File type not allowed", file.type);
    return;
  }

  warnIfFileIsLarge(file);

  processingImage.value = true;
  try {
    const imageData = await fileToDataURL(file);
    console.log("DataURL created");
    const image = await createImageFromData(imageData);
    // Compress and resize the image (removed for now)
    // const resizedImage = await resize(
    //   image,
    //   this.maxWidth,
    //   this.maxHeight,
    //   this.compression
    // );
    console.log("loaded image", image.width, image.height);

    emit("update:imageData", imageData);
    emit("change", { src: imageData, width: image.width, height: image.height, type: file.type });
  } catch (error) {
    console.error("Error loading image", error);
    showError(t("errors.imageLoadError"));
  } finally {
    processingImage.value = false;
  }
}

function createImageFromData(imageData: string): Promise<HTMLImageElement> {
  return new Promise(function (resolve, reject) {
    let image = new Image();
    try {
      image.src = imageData;
      image.onload = () => resolve(image);
      image.onerror = (error) => reject(error);
    } catch (error) {
      reject(error);
    }
  });
}

function isFileTypeAllowed(type: string) {
  return allowedFileTypes.some((allowedType) => {
    if (allowedType === "image/*" || allowedType === "video/*") {
      return type.startsWith(allowedType.split("/")[0]);
    }
    return type === allowedType;
  });
}
</script>

<style lang="scss">
.input-image-zone {
  max-width: 100%;
  position: relative;
  border: 2px solid var(--color-border);
  margin-top: 2px;
  background-color: var(--color-background-close);
  border-radius: 2px;
  text-align: center;
  color: var(--color-action);

  --target-width: v-bind("targetWidth");
  --target-height: v-bind("targetHeight");

  min-height: 4rem;
  aspect-ratio: var(--aspect-ratio, var(--target-width) / var(--target-height));
  transition: background-color 200ms;

  &.no-image {
    border: 2px dashed var(--color-border);
    box-shadow: inset 0 0px 0px var(--color-shadow-3);
    transition:
      box-shadow 200ms,
      background-color 200ms,
      border-color 200ms;

    &:hover {
      box-shadow: inset 0 1px 10px -3px var(--color-shadow-3);
      background-color: var(--color-background-close-hover);
      border-color: var(--color-action);
    }
  }

  &.is-hovering {
    color: var(--color-action-contrast);
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--color-action);
    pointer-events: none;
    opacity: 0;
    transition: opacity 200ms;
    z-index: 0;
  }
  &.is-hovering::after {
    opacity: 1;
  }

  &__text {
    pointer-events: none;
    font-size: 16px;
    transform: translate3d(0, 0, 0);
    z-index: 1;
  }

  &__main {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &.no-image {
      padding: 1rem;
    }
  }

  &__image {
    pointer-events: none;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    object-position: center center;
  }

  &__icon {
    font-size: 28px;
  }

  &__deletebutton {
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    background-color: var(--color-background-close);
    border: 1px solid var(--color-background-close);
    border-radius: 2px;
    height: 40px;
    width: 40px;

    &:hover {
      box-shadow: 0 2px 5px var(--color-shadow-1);
      background-color: var(--color-background-close-hover);
    }

    &:active,
    &:focus {
      background-color: var(--color-background-close);
      background-color: var(--color-background-close-hover);
    }
  }

  &__input {
    position: absolute;
    left: 0;
    top: 0;
    pointer-events: none;
    opacity: 0;
  }
}


.fadeup-enter-active {
  transition:
    opacity 0.5s,
    transform 0.5s;
}

.fadeup-leave-active,
.fadeup-leave-to {
  display: none;
  transition: none;
}

.fadeup-enter-from,
.fadeup-leave-to {
  opacity: 0;
  transform: translate3d(0, 20px, 0);
}
</style>